import type { FlattenedPortfolioItem } from "@/types";
import { sortBy } from "lodash-es";

/** A selection as portfolio item id → selected MWh. */
export type SelectedCounts = ReadonlyMap<number, number>;

/**
 * Groups whose selection must stay an oldest-first prefix: the registry debits a group's oldest
 * rows first, so a selection that skipped one would not be what the registry moves.
 *
 * @property groupKey - The group a row belongs to; null leaves the row outside any ladder
 * @property rank - Orders a group's rows oldest first, compared as strings; a total order within a group
 */
export interface SelectionLadder {
  groupKey(item: FlattenedPortfolioItem): string | null;
  rank(item: FlattenedPortfolioItem): string;
}

interface LadderPosition {
  key: string;
  group: readonly FlattenedPortfolioItem[];
  index: number;
}

/**
 * The ladder groups of a set of rows.
 *
 * @property groups - Each group's member rows, oldest first
 * @property positions - Where each member row sits, by portfolio item id
 * @property maxOf - The most of a member the selection may take: what "taken in full" means
 */
export interface Ladders {
  readonly groups: ReadonlyMap<string, readonly FlattenedPortfolioItem[]>;
  readonly positions: ReadonlyMap<number, LadderPosition>;
  readonly maxOf: (item: FlattenedPortfolioItem) => number;
}

export const NO_LADDERS: Ladders = {
  groups: new Map(),
  positions: new Map(),
  maxOf: (item) => item.count,
};

/**
 * Groups the rows `isMember` admits and that have something to select under `maxOf`. Rows left out
 * never hold a group back: a selection may take a newer row although an older non-member one is not
 * selected.
 */
export const buildLadders = (
  items: readonly FlattenedPortfolioItem[],
  ladder: SelectionLadder,
  isMember: (item: FlattenedPortfolioItem) => boolean,
  maxOf: (item: FlattenedPortfolioItem) => number
): Ladders => {
  const groups = new Map<string, FlattenedPortfolioItem[]>();
  for (const item of items) {
    const key = ladder.groupKey(item);
    if (key === null || !isMember(item) || maxOf(item) <= 0) continue;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  const positions = new Map<number, LadderPosition>();
  for (const [key, group] of groups) {
    // Code-point order, as the server sorts the rank strings.
    group.sort((a, b) => compareStrings(ladder.rank(a), ladder.rank(b)));
    group.forEach((item, index) =>
      positions.set(item.id, { key, group, index })
    );
  }
  return { groups, positions, maxOf };
};

/**
 * Restores the oldest-first prefix around each changed row, in order: a selected row takes every older
 * member of its group in full, and a row not taken in full drops every newer one.
 */
export const settleLadders = (
  counts: SelectedCounts,
  ladders: Ladders,
  changedIds: Iterable<number>
): Map<number, number> => {
  const next = new Map(counts);
  for (const id of changedIds) {
    const position = ladders.positions.get(id);
    if (!position) continue;
    const { group, index } = position;
    const count = next.get(id);
    if (count !== undefined) {
      for (const older of group.slice(0, index))
        next.set(older.id, ladders.maxOf(older));
    }
    if (count === undefined || count < ladders.maxOf(group[index])) {
      for (const newer of group.slice(index + 1)) next.delete(newer.id);
    }
  }
  return next;
};

/**
 * Trims every group to a valid prefix after counts changed underneath it (a refetch, a URL): the rows
 * after the first one not taken in full drop.
 */
export const normalizeLadders = (
  counts: SelectedCounts,
  ladders: Ladders
): Map<number, number> => {
  const next = new Map(counts);
  for (const group of ladders.groups.values()) {
    const frontier = group.findIndex(
      (member) => (next.get(member.id) ?? 0) < ladders.maxOf(member)
    );
    if (frontier < 0) continue;
    for (const newer of group.slice(frontier + 1)) next.delete(newer.id);
  }
  return next;
};

/** Whether every older member of the row's group is selected in full; true for rows outside any group. */
export const olderTakenInFull = (
  counts: SelectedCounts,
  ladders: Ladders,
  itemId: number
): boolean => {
  const position = ladders.positions.get(itemId);
  return (
    !position ||
    position.group
      .slice(0, position.index)
      .every((older) => counts.get(older.id) === ladders.maxOf(older))
  );
};

/**
 * The rows in the given order, except that each group's members trade places among the positions
 * they hold so they follow the group's own order. A walk over the result never reaches a member
 * before an older one of the same group.
 */
export const inLadderOrder = (
  rows: readonly FlattenedPortfolioItem[],
  ladders: Ladders
): FlattenedPortfolioItem[] => {
  const membersByGroup = new Map<
    string,
    { slot: number; index: number; row: FlattenedPortfolioItem }[]
  >();
  rows.forEach((row, slot) => {
    const position = ladders.positions.get(row.id);
    if (!position) return;
    const member = { slot, index: position.index, row };
    const members = membersByGroup.get(position.key);
    if (members) members.push(member);
    else membersByGroup.set(position.key, [member]);
  });
  const ordered = [...rows];
  for (const members of membersByGroup.values()) {
    const inGroupOrder = sortBy(members, "index");
    members.forEach(({ slot }, index) => {
      ordered[slot] = inGroupOrder[index].row;
    });
  }
  return ordered;
};

const compareStrings = (a: string, b: string): number =>
  a < b ? -1 : a > b ? 1 : 0;
